import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiService } from '../services/api.service';
import { CloseCashWizard } from '../pages/cash/CloseCashWizard';
import { formatMoney } from '../pages/treasury/treasuryUi';

const SESSION_KEY = 'cashSessionId';
const SESSION_EVENT = 'cash-session-changed';
const CLOSE_EVENT = 'cash-session-close-request';

function notifyCashSessionChanged() {
  window.dispatchEvent(new Event(SESSION_EVENT));
}

/** Dispara el wizard de cierre TX7 (Mi caja / banner). */
export function requestCashSessionClose() {
  window.dispatchEvent(new Event(CLOSE_EVENT));
}

export function useCashSession() {
  const [session, setSession] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const s = await apiService.getMyCashSession();
      setSession(s);
      if (s?.id) localStorage.setItem(SESSION_KEY, s.id);
      else localStorage.removeItem(SESSION_KEY);
      notifyCashSessionChanged();
    } catch {
      setSession(null);
      localStorage.removeItem(SESSION_KEY);
      notifyCashSessionChanged();
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const s = await apiService.getMyCashSession();
        if (cancelled) return;
        setSession(s);
        if (s?.id) localStorage.setItem(SESSION_KEY, s.id);
        else localStorage.removeItem(SESSION_KEY);
      } catch {
        if (!cancelled) {
          setSession(null);
          localStorage.removeItem(SESSION_KEY);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    const onChanged = () => {
      apiService
        .getMyCashSession()
        .then((s) => {
          setSession(s);
          if (s?.id) localStorage.setItem(SESSION_KEY, s.id);
          else localStorage.removeItem(SESSION_KEY);
        })
        .catch(() => {
          setSession(null);
          localStorage.removeItem(SESSION_KEY);
        });
    };
    window.addEventListener(SESSION_EVENT, onChanged);
    return () => {
      cancelled = true;
      window.removeEventListener(SESSION_EVENT, onChanged);
    };
  }, []);

  return {
    session,
    loading,
    refresh,
    sessionId: session?.id ?? null,
    cashRegisterId: session?.cashRegisterId ?? session?.cashRegister?.id ?? null,
  };
}

/**
 * TX3/TX7 — indicador de estado en el header.
 * Abrir / operar: /cash. Cierre: CloseCashWizard (evento).
 */
export function CashSessionBar() {
  const { session, loading } = useCashSession();

  if (loading) return null;

  const branchLabel = session?.cashRegister?.branch?.name ?? '—';
  const cajaLabel =
    session?.cashRegister?.number != null ? `Caja ${session.cashRegister.number}` : 'Caja';
  const balanceLabel =
    session?.systemBalance != null ? formatMoney(Number(session.systemBalance)) : null;

  return (
    <>
      <div className="flex min-w-0 items-center gap-2">
        {session ? (
          <Link
            to="/cash"
            className={`inline-flex max-w-full items-center gap-2 rounded-md border px-2.5 py-1 text-xs transition hover:opacity-90 ${
              session.staleDay
                ? 'border-amber-200 bg-amber-50 text-amber-950'
                : 'border-emerald-200 bg-emerald-50 text-emerald-900'
            }`}
            title="Ir a Mi caja"
          >
            <span
              className={`h-1.5 w-1.5 shrink-0 rounded-full ${
                session.staleDay ? 'bg-amber-500' : 'bg-emerald-500'
              }`}
              aria-hidden
            />
            <span className="truncate font-medium">
              {cajaLabel} · {branchLabel}
            </span>
            <span className="shrink-0 font-semibold uppercase tracking-wide">
              {session.staleDay ? 'AYER' : 'ABIERTA'}
            </span>
            {balanceLabel && (
              <span className="hidden sm:inline shrink-0 tabular-nums font-semibold">{balanceLabel}</span>
            )}
          </Link>
        ) : (
          <Link
            to="/cash?open=1"
            className="inline-flex h-8 items-center rounded-md border border-gray-200 bg-white px-3 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50"
          >
            Abrir caja
          </Link>
        )}
      </div>
      <CloseCashWizard />
    </>
  );
}

export function getStoredCashSessionId(): string | null {
  return localStorage.getItem(SESSION_KEY);
}

/** Franja bajo el header cuando la caja quedó del día anterior. */
export function CashSessionStaleBanner() {
  const { session, loading } = useCashSession();
  if (loading || !session?.staleDay) return null;

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-950 sm:px-6 lg:px-8">
      <span className="inline-flex items-center gap-1.5 font-semibold">
        <span className="h-1.5 w-1.5 rounded-full bg-amber-500" aria-hidden />
        Esta caja continúa abierta desde ayer.
      </span>
      <span className="min-w-0 flex-1 text-amber-900/90">
        {session.staleDayMessage ?? 'Revisá movimientos y cerrá antes de abrir la de hoy.'}
      </span>
      <Link
        to="/cash"
        className="shrink-0 rounded-md bg-amber-800 px-2.5 py-1 font-medium text-white hover:bg-amber-900"
      >
        Revisar caja
      </Link>
    </div>
  );
}
